const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off']);

function parseFeatureFlag(name: string, rawValue: string | undefined, defaultValue: boolean) {
  if (!rawValue) return defaultValue;

  const normalizedValue = rawValue.trim().toLowerCase();
  if (TRUE_VALUES.has(normalizedValue)) return true;
  if (FALSE_VALUES.has(normalizedValue)) return false;

  if (import.meta.env.DEV) {
    console.warn(
      `[featureFlags] Invalid value for ${name}: "${rawValue}". Falling back to ${defaultValue}.`
    );
  }

  return defaultValue;
}

// Temporary release flags for incomplete or non-MVP surfaces.
export const featureFlags = Object.freeze({
  teamManagement: parseFeatureFlag(
    'VITE_FEATURE_TEAM_MANAGEMENT',
    import.meta.env.VITE_FEATURE_TEAM_MANAGEMENT,
    false
  ),
  rubrics: parseFeatureFlag('VITE_FEATURE_RUBRICS', import.meta.env.VITE_FEATURE_RUBRICS, true),
  verticalAlignment: parseFeatureFlag(
    'VITE_FEATURE_VERTICAL_ALIGNMENT',
    import.meta.env.VITE_FEATURE_VERTICAL_ALIGNMENT,
    false
  ),
  librarySurfaces: parseFeatureFlag(
    'VITE_FEATURE_LIBRARY_SURFACES',
    import.meta.env.VITE_FEATURE_LIBRARY_SURFACES,
    false
  ),
  resources: parseFeatureFlag(
    'VITE_FEATURE_RESOURCES',
    import.meta.env.VITE_FEATURE_RESOURCES,
    false
  ),
  googleSlidesImport: parseFeatureFlag(
    'VITE_FEATURE_GOOGLE_SLIDES_IMPORT',
    import.meta.env.VITE_FEATURE_GOOGLE_SLIDES_IMPORT,
    true
  ),
  googleDocsImport: parseFeatureFlag(
    'VITE_FEATURE_GOOGLE_DOCS_IMPORT',
    import.meta.env.VITE_FEATURE_GOOGLE_DOCS_IMPORT,
    true
  ),
  googleSlidesAccessibilityPreflight: parseFeatureFlag(
    'VITE_FEATURE_GOOGLE_SLIDES_ACCESSIBILITY_PREFLIGHT',
    import.meta.env.VITE_FEATURE_GOOGLE_SLIDES_ACCESSIBILITY_PREFLIGHT,
    false
  ),
  // When true, imported Google file links (Docs, Slides) have their text
  // extracted for the LLM and silently refreshed when the AI chat panel opens.
  linkContentExtraction: parseFeatureFlag(
    'VITE_FEATURE_GOOGLE_LINK_CONTENT_EXTRACTION',
    import.meta.env.VITE_FEATURE_GOOGLE_LINK_CONTENT_EXTRACTION,
    true
  ),
  googleLogin: parseFeatureFlag(
    'VITE_FEATURE_GOOGLE_LOGIN',
    import.meta.env.VITE_FEATURE_GOOGLE_LOGIN,
    true
  ),
  generalChatbot: parseFeatureFlag(
    'VITE_FEATURE_GENERAL_CHATBOT',
    import.meta.env.VITE_FEATURE_GENERAL_CHATBOT,
    false
  ),
  profile: parseFeatureFlag('VITE_FEATURE_PROFILE', import.meta.env.VITE_FEATURE_PROFILE, false),
  scoperAiDevDialog: parseFeatureFlag(
    'VITE_FEATURE_SCOPER_AI_DEV_DIALOG',
    import.meta.env.VITE_FEATURE_SCOPER_AI_DEV_DIALOG,
    true
  ),
  curriculumPdfExport: parseFeatureFlag(
    'VITE_FEATURE_CURRICULUM_PDF_EXPORT',
    import.meta.env.VITE_FEATURE_CURRICULUM_PDF_EXPORT,
    false
  ),
  docxExport: parseFeatureFlag(
    'VITE_FEATURE_DOCX_EXPORT',
    import.meta.env.VITE_FEATURE_DOCX_EXPORT,
    false
  ),
  documentZoomUsesCssZoomProperty: parseFeatureFlag(
    'VITE_FEATURE_DOCUMENT_ZOOM_USES_CSS_ZOOM_PROPERTY',
    import.meta.env.VITE_FEATURE_DOCUMENT_ZOOM_USES_CSS_ZOOM_PROPERTY,
    false
  ),
  // When true, show the Commonly brand logo; when false (default), show CoPlan.
  useCommonlyLogo: parseFeatureFlag(
    'VITE_FEATURE_USE_COMMONLY_LOGO',
    import.meta.env.VITE_FEATURE_USE_COMMONLY_LOGO,
    false
  ),
  // When true (default), the curriculum sidebar renders the full course → unit →
  // lesson hierarchy as an expandable nav tree. When false, it falls back to the
  // legacy one-level-at-a-time drill-down nav (UnitsNav / LessonsNav).
  curriculumNavTree: parseFeatureFlag(
    'VITE_FEATURE_CURRICULUM_NAV_TREE',
    import.meta.env.VITE_FEATURE_CURRICULUM_NAV_TREE,
    true
  ),
  standardsLibraryV2: parseFeatureFlag(
    'VITE_FEATURE_STANDARDS_LIBRARY_V2',
    import.meta.env.VITE_FEATURE_STANDARDS_LIBRARY_V2,
    true
  ),
});

export type FeatureFlagName = keyof typeof featureFlags;

export function isFeatureEnabled(flag: FeatureFlagName) {
  return featureFlags[flag];
}
